import { createMachine, assign } from 'xstate';
import * as yup from 'yup';
import { capitalize } from '@crazyegginc/hatch';
import { isValidCssSelector } from '@crazyegginc/js-utils';

import { LOGICALS } from '/src/features/_global/constants';
import {
  GOAL_TRIGGER_TYPES,
  PIXEL_API_LIST,
  ENTER_YOUR_OWN_EVENT,
  ECOMMERCE_LIST,
  ECOMMERCE_EVENTS_LIST_TYPE,
  ANY_PIXEL_ID,
  ELEMENT_SELECTOR_TYPES,
  GOAL_TRACKING_OPTIONS,
  GOAL_VALUE_TYPES,
} from '../constants';

import { contextToArgs, parseTriggers, resolveTriggerKind } from '/src/features/goals/utils';
import { getValidationErrors } from '/src/utils';
import { normalizeUrl } from '/src/utils/url';
import { superLenientUrlRegex, noAsteriskRegex } from '/src/utils/regex';
import { emptyAsNull } from '/src/utils/string';

import { changeInteractiveMode, changePopupsState } from '/src/services/web-editor';
import { getPixelEventOptions } from '../components/wizard/triggers/pixelUtils';

const { CLICK, FORM_SUBMISSION, URL_VISIT, CODE, PIXEL_EVENT, API, ECOMMERCE_EVENT, GA4_EVENT } = GOAL_TRIGGER_TYPES;

const CLICK_OR_FORM_TRIGGERS = [CLICK, FORM_SUBMISSION];
const TARGET_URL_OPTIONAL_TRIGGERS = [PIXEL_EVENT, GA4_EVENT, CODE, API, ECOMMERCE_EVENT];
const WILDCARD_URL_OPTIONAL_TRIGGERS = [URL_VISIT, CODE, PIXEL_EVENT, GA4_EVENT, API, ECOMMERCE_EVENT];

export const STATES = {
  REDIRECT_EDIT: 'redirectEdit',
  INITIALIZE_EDIT: 'initializeEdit',
  INITIALIZE_VALUE: 'initializeValue',
  GOAL_NAME: 'goalName',
  GOAL_SITE: 'goalSite',
  MANAGE_TRIGGERS: 'manageTriggers',
  MANAGE_VALUE: 'manageValue',
  SAVING_VALUE: 'savingValue',
  SAVING_GOAL: 'savingGoal',
  ERRORED: 'errored',
  TIMED_OUT: 'timedout',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  INITIALIZE: 'initialize',
  INITIALIZE_ERROR: 'initializeError',
};

export const GOAL_ACTIONS = {
  GOAL_LOADED: 'GOAL_LOADED',
  CHANGE: 'CHANGE',
  SELECT_SITE: 'SELECT_SITE',
  TOGGLE_TRIGGER: 'TOGGLE_TRIGGER',
  ADD_TRIGGER: 'ADD_TRIGGER',
  DELETE_TRIGGER: 'DELETE_TRIGGER',
  SET_TRIGGER: 'SET_TRIGGER',
  SET_SYNC_SELECTORS: 'SET_SYNC_SELECTORS',
  SET_SELECTOR: 'SET_SELECTOR',
  SET_INTERACTIVE: 'SET_INTERACTIVE',
  CHANGE_POPUPS_STATE: 'CHANGE_POPUPS_STATE',
  NEXT: 'NEXT',
  SET_SYNC_VALUES: 'SET_SYNC_VALUES',
  SET_TRIGGER_VALUE: 'SET_TRIGGER_VALUE',
  BACK: 'BACK',
  VALUE_SUCCESS: 'VALUE_SUCCESS',
  VALUE_ERROR: 'VALUE_ERROR',
  GOAL_SUCCESS: 'GOAL_SUCCESS',
  GOAL_ERROR: 'GOAL_ERROR',
  CANCEL: 'CANCEL',
  RETRY: 'RETRY',
};

function getSelectedSite() {
  return new URLSearchParams(window.location.search).get('site');
}

function withSelectedSiteMatch(schema) {
  const selectedSite = getSelectedSite();
  if (!selectedSite) return schema;

  return schema.matches(
    new RegExp(`${selectedSite.replace(/\./g, '\\.')}`, 'i'),
    `The provided URL must be on ${selectedSite}.`,
  );
}

function buildSiteUrlSchema({ allowWildcard = false } = {}) {
  let schema = yup
    .string()
    .required('The URL is required.')
    .matches(superLenientUrlRegex, 'This URL is not valid. Please correct it and try again.');
  schema = withSelectedSiteMatch(schema);

  if (!allowWildcard) {
    schema = schema.matches(
      noAsteriskRegex,
      'Please enter a URL without a wildcard. You can select the wildcard option below.',
    );
  }

  return schema;
}

function buildWildcardUrlSchema() {
  let schema = yup.string().required('The wildcard URL is a required field.');
  schema = withSelectedSiteMatch(schema);
  return schema.matches(/\*/, 'The wildcard URL must contain at least one wildcard segment denoted by "*".');
}

function buildCssSelectorSchema() {
  return yup
    .string()
    .required('Please enter a CSS selector.')
    .max(10000, 'Your CSS selector exceeds the maximum length of 10,000 characters.')
    .test('css-selector', 'Please enter a valid CSS selector.', (value) => isValidCssSelector(value));
}

function buildTrackingOptionSchema() {
  return yup
    .string()
    .oneOf([...Object.values(GOAL_TRACKING_OPTIONS)])
    .required();
}

function buildElementSelectorTypeSchema() {
  return yup.string().oneOf([ELEMENT_SELECTOR_TYPES.SPECIFIC, ELEMENT_SELECTOR_TYPES.MANUAL]).required();
}

const triggerSchema = yup.object().shape({
  autoName: yup.string().nullable(),
  name: yup.string().nullable(),
  trigger: yup.string().oneOf([...Object.values(GOAL_TRIGGER_TYPES)]),
  code: yup.string().when('trigger', {
    is: CODE,
    then: yup.string().required(),
    otherwise: yup.string().notRequired(),
  }),
  targetUrl: yup.string().when('trigger', {
    is: (value) => TARGET_URL_OPTIONAL_TRIGGERS.includes(value),
    then: yup.string().notRequired(),
    otherwise: (schema) => {
      return schema.when('trigger', {
        is: URL_VISIT,
        then: () => buildSiteUrlSchema({ allowWildcard: true }),
        otherwise: () => buildSiteUrlSchema(),
      });
    },
  }),
  wildcardUrl: yup.string().when('trigger', {
    is: (value) => WILDCARD_URL_OPTIONAL_TRIGGERS.includes(value),
    then: yup.string().nullable().notRequired(),
    otherwise: (innerSchema) => {
      return innerSchema.when('trackingOption', {
        is: GOAL_TRACKING_OPTIONS.WILDCARD,
        then: () => buildWildcardUrlSchema(),
        otherwise: yup.string().nullable().notRequired(),
      });
    },
  }),
  trackingOption: yup.string().when('trigger', {
    is: (value) => CLICK_OR_FORM_TRIGGERS.includes(value),
    then: () => buildTrackingOptionSchema(),
    otherwise: yup.string().notRequired(),
  }),
  elementSelectorType: yup.string().when('trigger', {
    is: (value) => CLICK_OR_FORM_TRIGGERS.includes(value),
    then: () => buildElementSelectorTypeSchema(),
    otherwise: yup.string().notRequired(),
  }),
  filter: yup.string(),
  // click and form_submission only checks
  desktopSelector: yup.string().when('trigger', {
    is: (value) => CLICK_OR_FORM_TRIGGERS.includes(value),
    then: () => buildCssSelectorSchema(),
    otherwise: yup.string().notRequired(),
  }),
  tabletSelector: yup.string().when('trigger', {
    is: (value) => CLICK_OR_FORM_TRIGGERS.includes(value),
    then: () => buildCssSelectorSchema(),
    otherwise: yup.string().notRequired(),
  }),
  phoneSelector: yup.string().when('trigger', {
    is: (value) => CLICK_OR_FORM_TRIGGERS.includes(value),
    then: () => buildCssSelectorSchema(),
    otherwise: yup.string().notRequired(),
  }),
  pixelApi: yup.string().when('trigger', {
    is: PIXEL_EVENT,
    then: yup
      .string()
      .oneOf([...Object.values(PIXEL_API_LIST)])
      .required('Please select one.'),
    otherwise: yup.string().notRequired(),
  }),
  pixelId: yup.string().when('trigger', {
    is: PIXEL_EVENT,
    then: yup.string().required('Please enter a value.'),
    otherwise: yup.string().notRequired(),
  }),
  pixelEvent: yup.string().notRequired(),
  pixelCustomEvent: yup.string().when(['trigger', 'pixelEvent'], {
    is: (trigger, pixelEvent) => trigger === PIXEL_EVENT && pixelEvent === ENTER_YOUR_OWN_EVENT,
    then: yup.string().required('Please enter a value.'),
    otherwise: yup.string().notRequired(),
  }),
  ga4MeasurementId: yup.string().when('trigger', {
    is: GA4_EVENT,
    then: yup
      .string()
      .trim()
      .required('Please enter a value.')
      .matches(/^G-[A-Z0-9]+$/i, 'Please enter a valid GA4 measurement ID (for example, G-123HSDF12).'),
    otherwise: yup.string().notRequired(),
  }),
  ga4Event: yup.string().when('trigger', {
    is: GA4_EVENT,
    then: yup
      .string()
      .trim()
      .required('Please enter a value.')
      .matches(
        /^(?!firebase_|google_|ga_)[A-Za-z][A-Za-z0-9_]{0,39}$/,
        'Please enter a valid GA4 event name (letters, numbers, underscores; start with a letter; max 40 chars).',
      ),
    otherwise: yup.string().notRequired(),
  }),
  ecommerceApi: yup.string().when('trigger', {
    is: ECOMMERCE_EVENT,
    then: yup
      .string()
      .oneOf([...Object.values(ECOMMERCE_LIST)])
      .required('Please select one.'),
    otherwise: yup.string().notRequired(),
  }),
  ecommerceEvent: yup.string().notRequired(),
});

const goalSchema = yup.object().shape({
  name: yup.string(),
  siteId: yup.number().integer().positive(),
  triggers: yup.array().of(triggerSchema),
  syncValues: yup.boolean().optional().nullable(),
});

const goalTriggerValueSchema = yup.object().shape({
  valueType: yup
    .string()
    .oneOf([GOAL_VALUE_TYPES.FIXED, GOAL_VALUE_TYPES.ELEMENT, GOAL_VALUE_TYPES.CODE, GOAL_VALUE_TYPES.NO_VALUE])
    .required(),
  fixedValue: yup.object().when('valueType', {
    is: (value) => value === GOAL_VALUE_TYPES.FIXED,
    then: yup
      .object()
      .shape({
        currency: yup.string().oneOf(Intl.supportedValuesOf('currency')).required(),
        worth: yup
          .string()
          .required('Please enter a value.')
          .matches(/^\d+(\.\d+)?$/, 'Please enter a valid numeric value.'),
      })
      .required(),
    otherwise: yup.object().nullable(true),
  }),
  elementValue: yup.object().when('valueType', {
    is: (value) => value === GOAL_VALUE_TYPES.ELEMENT,
    then: yup.object().shape({
      currency: yup.string().oneOf(Intl.supportedValuesOf('currency')).required(),
      condition: yup
        .object()
        .shape({
          trackingOption: buildTrackingOptionSchema(),
          elementSelectorType: buildElementSelectorTypeSchema(),
          pageUrl: yup.lazy(() => buildSiteUrlSchema()),
          wildcardUrl: yup.string().when('trackingOption', {
            is: (value) => value === GOAL_TRACKING_OPTIONS.WILDCARD,
            then: () => buildWildcardUrlSchema(),
            otherwise: yup.string().nullable().notRequired(),
          }),
          selectorForDesktop: buildCssSelectorSchema(),
          selectorForTablet: buildCssSelectorSchema(),
          selectorForPhone: buildCssSelectorSchema(),
        })
        .required(),
    }),
    otherwise: yup.object().nullable(true),
  }),
});

const valueSchema = yup.object().shape({
  syncValues: yup.boolean().nullable(),
  triggers: yup.array().of(goalTriggerValueSchema),
});

const emptyTrigger = {
  autoName: '',
  name: '',
  trigger: '',
  targetUrl: '',
  devices: [],
  trackingOption: GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE,
  elementSelectorType: ELEMENT_SELECTOR_TYPES.SPECIFIC,
  filter: null,
  expanded: true,
  syncSelectors: true,
  pixelApi: '',
  pixelId: '',
  pixelEvent: '',
  pixelCustomEvent: '',
  ga4MeasurementId: '',
  ga4Event: '',
  valueType: GOAL_VALUE_TYPES.NO_VALUE,
  fixedValue: null,
  elementValue: null,
  ecommerceApi: '',
  ecommerceEvent: ECOMMERCE_EVENTS_LIST_TYPE.ADD_TO_CART,
};

export const initialGoalContext = {
  step: 1,
  nextText: 'Create goal',
  dirty: false,
  errors: {},
  payload: {
    name: '',
    siteId: null,
    triggers: [emptyTrigger],
    syncValues: true,
  },
  goalId: null,
  hidePopups: false,
  isEditing: false,
};

export function getDefaultGoalValue(field, context) {
  if (field.toLowerCase() === 'element') {
    return {
      currency: context.defaultCurrency,
      condition: {
        elementSelectorType: ELEMENT_SELECTOR_TYPES.SPECIFIC,
        trackingOption: GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE,
      },
      syncSelectors: true,
    };
  }
  if (field.toLowerCase() === 'fixed') {
    return {
      currency: context.defaultCurrency,
    };
  }
  return undefined;
}

function isSelectorsSynced(options) {
  return options.desktopSelector === options.tabletSelector && options.desktopSelector === options.phoneSelector;
}

function elementBasedGoalFields(version = 3, options) {
  return {
    name: version === 3 ? (options?.name ?? null) : undefined,
    auto_name: version === 3 ? (options?.autoName ?? null) : undefined,
    selector_for_desktop: options.desktopSelector,
    selector_for_tablet: options.tabletSelector,
    selector_for_phone: options.phoneSelector,
    selector_source: options.elementSelectorType.toLowerCase(),
    source_url:
      options.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD
        ? options.wildcardUrl
          ? normalizeUrl(options.wildcardUrl, { appendSlash: false })
          : ''
        : normalizeUrl(options.targetUrl, { appendSlash: false }),
    preview_url:
      options.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD
        ? normalizeUrl(options.targetUrl, { appendSlash: false })
        : null,
    sync_selectors: isSelectorsSynced(options),
  };
}

export function serializeTrigger({ trigger, version = 3, ...options }) {
  if (trigger === CLICK || trigger === FORM_SUBMISSION) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [
            {
              on_any_page: [GOAL_TRACKING_OPTIONS.ANY_PAGE, GOAL_TRACKING_OPTIONS.WILDCARD].includes(
                options.trackingOption,
              )
                ? true
                : false,
              on_page:
                options.trackingOption === GOAL_TRACKING_OPTIONS.WILDCARD
                  ? options.wildcardUrl
                    ? normalizeUrl(options.wildcardUrl, { appendSlash: false })
                    : ''
                  : normalizeUrl(options.targetUrl, { appendSlash: false }),
              ...(version === 2
                ? { value: { ...elementBasedGoalFields(version, options) } }
                : elementBasedGoalFields(version, options)),
            },
          ],
        },
      ],
    });
  } else if (trigger === URL_VISIT) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [options.targetUrl],
        },
      ],
    });
  } else if (trigger === CODE) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [options.code],
        },
      ],
    });
  } else if (trigger === PIXEL_EVENT) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [
            {
              on_any_page: true,
              api: options.pixelApi,
              id: emptyAsNull(options.pixelId === ANY_PIXEL_ID ? null : options.pixelId),
              event: emptyAsNull(
                options.pixelEvent === ENTER_YOUR_OWN_EVENT ? options.pixelCustomEvent : options.pixelEvent,
              ),
            },
          ],
        },
      ],
    });
  } else if (trigger === GA4_EVENT) {
    const ga4MeasurementId = (options.ga4MeasurementId ?? '').trim();
    const ga4Event = (options.ga4Event ?? '').trim();

    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: 'pixel_event', // this is correct. GA4 events are modeled as pixel events in the backend
          multiple_values: 'or',
          values: [
            {
              on_any_page: true,
              api: 'ga4',
              id: ga4MeasurementId,
              event: ga4Event,
            },
          ],
        },
      ],
    });
  } else if (trigger === ECOMMERCE_EVENT) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [
            {
              on_any_page: true,
              api: options.ecommerceApi,
              event: options.ecommerceEvent,
            },
          ],
        },
      ],
    });
  } else if (trigger === API) {
    return JSON.stringify({
      version,
      operator: LOGICALS.OR,
      conditions: [
        {
          criteria: trigger.toLowerCase(),
          multiple_values: 'or',
          values: [],
        },
      ],
    });
  }
}

export function deserializeTrigger(rawFilter) {
  if (!rawFilter || (typeof rawFilter !== 'string' && typeof rawFilter !== 'object')) {
    return rawFilter;
  }

  let filter;

  try {
    filter = typeof rawFilter === 'string' ? JSON.parse(rawFilter) : rawFilter;
  } catch {
    return rawFilter;
  }

  const condition = Array.isArray(filter?.conditions) ? filter.conditions[0] : null;
  if (!condition) {
    return rawFilter;
  }

  const values = Array.isArray(condition?.values) ? condition.values[0] : undefined;

  const triggerKind = resolveTriggerKind({ filter: rawFilter });

  if (triggerKind === CLICK || triggerKind === FORM_SUBMISSION) {
    return {
      name: values?.name,
      autoName: values?.auto_name,
      trigger: triggerKind,
      trackingOption:
        values.source_url.indexOf('*') >= 0 && values?.preview_url && values?.on_any_page
          ? GOAL_TRACKING_OPTIONS.WILDCARD
          : values?.on_any_page
            ? GOAL_TRACKING_OPTIONS.ANY_PAGE
            : values?.on_page
              ? GOAL_TRACKING_OPTIONS.SPECIFIC_PAGE
              : undefined,
      desktopSelector: values?.selector_for_desktop,
      tabletSelector: values?.selector_for_tablet,
      phoneSelector: values?.selector_for_phone,
      elementSelectorType: values?.selector_source?.toUpperCase?.(),
      targetUrl: values?.preview_url ? values?.preview_url : values?.source_url,
      wildcardUrl: values?.preview_url ? values?.source_url : null,
      syncSelectors: isSelectorsSynced(values),
    };
  } else if (triggerKind === URL_VISIT) {
    return {
      trigger: triggerKind,
      targetUrl: values,
    };
  } else if (triggerKind === CODE) {
    return {
      trigger: triggerKind,
      code: values,
    };
  } else if (triggerKind === PIXEL_EVENT) {
    const isCustom = !getPixelEventOptions(values?.api)
      .map((x) => x.value)
      .includes(values?.event);

    const isEmpty = values?.event === null || values?.event === '';

    return {
      trigger: triggerKind,
      pixelApi: values?.api ?? '',
      pixelId: values?.id ? values.id : ANY_PIXEL_ID,
      pixelEvent: isCustom && !isEmpty ? ENTER_YOUR_OWN_EVENT : (values?.event ?? '') || '',
      pixelCustomEvent: (isCustom ? values?.event : '') ?? '',
    };
  } else if (triggerKind === GA4_EVENT) {
    return {
      trigger: triggerKind,
      ga4MeasurementId: values?.id ?? '',
      ga4Event: values?.event ?? '',
    };
  } else if (triggerKind === ECOMMERCE_EVENT) {
    return {
      trigger: triggerKind,
      ecommerceApi: values?.api ?? '',
      ecommerceEvent: values?.event ?? '',
    };
  } else if (triggerKind === API) {
    return {
      trigger: triggerKind,
    };
  }
}

export const createGoalMachine = (initialState = STATES.GOAL_NAME, initialPayload = {}) => {
  return createMachine(
    {
      id: 'goalWizard',
      initial: initialState,
      predictableActionArguments: true,
      context: {
        ...initialGoalContext,
        payload: {
          ...initialGoalContext.payload,
          ...initialPayload,
        },
      },
      states: {
        [STATES.REDIRECT_EDIT]: {},
        [STATES.INITIALIZE_EDIT]: {
          on: {
            [GOAL_ACTIONS.GOAL_LOADED]: {
              actions: assign({
                payload: (ctx, e) => {
                  const triggers = parseTriggers(e.value.triggers.filter((t) => !t.deletedAt));
                  const hasEcommerceOrApiTrigger = triggers.some((t) => [ECOMMERCE_EVENT, API].includes(t.trigger));
                  return {
                    ...e.value,
                    triggers,
                    syncValues: hasEcommerceOrApiTrigger ? false : e.value.syncValues,
                  };
                },
              }),
              target: STATES.MANAGE_TRIGGERS,
            },
          },
        },
        [STATES.INITIALIZE_VALUE]: {
          on: {
            [GOAL_ACTIONS.GOAL_LOADED]: {
              actions: assign({
                payload: (ctx, e) => {
                  const triggers = parseTriggers(e.value.triggers);
                  const hasEcommerceOrApiTrigger = triggers
                    .filter((t) => !t.deletedAt)
                    .some((t) => [ECOMMERCE_EVENT, API].includes(t.trigger));
                  return {
                    ...e.value,
                    triggers,
                    syncValues: hasEcommerceOrApiTrigger ? false : e.value.syncValues,
                  };
                },
              }),
              target: STATES.MANAGE_VALUE,
            },
          },
        },
        [STATES.GOAL_NAME]: {
          always: [
            {
              target: STATES.GOAL_SITE,
              cond: (ctx) => ctx.payload.name.length > 0,
            },
          ],
          on: {
            [GOAL_ACTIONS.CHANGE]: {
              target: STATES.GOAL_SITE,
              cond: (ctx, e) => e.value.length > 0,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => ({
                    ...ctx.payload,
                    [e.key]: e.value,
                  }),
                }),
              ],
            },
          },
        },
        [STATES.GOAL_SITE]: {
          always: [
            {
              target: STATES.MANAGE_TRIGGERS,
              cond: (ctx) => !ctx.payload.siteId && !!ctx.siteId,
              actions: [
                assign({
                  payload: (ctx) => ({
                    ...ctx.payload,
                    siteId: ctx.siteId,
                  }),
                }),
              ],
            },
            {
              target: STATES.MANAGE_TRIGGERS,
              cond: (ctx) => !!ctx.payload.siteId,
            },
          ],
          on: {
            [GOAL_ACTIONS.SELECT_SITE]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  payload: (ctx, e) => ({
                    ...ctx.payload,
                    siteId: e.site.id,
                  }),
                }),
              ],
            },
          },
        },
        [STATES.MANAGE_TRIGGERS]: {
          entry: assign({
            errors: (ctx) => getValidationErrors(goalSchema, ctx.payload),
          }),
          on: {
            [GOAL_ACTIONS.CHANGE]: [
              {
                target: STATES.MANAGE_TRIGGERS,
                cond: (ctx, e) => e.value.length > 0,
                actions: [
                  assign({
                    dirty: true,
                    payload: (ctx, e) => ({
                      ...ctx.payload,
                      [e.key]: e.value,
                    }),
                  }),
                ],
              },
              // TODO: simplify this
              {
                target: STATES.GOAL_NAME,
                actions: [
                  assign({
                    dirty: true,
                    payload: (ctx, e) => ({
                      ...ctx.payload,
                      [e.key]: e.value,
                    }),
                  }),
                ],
              },
            ],
            [GOAL_ACTIONS.TOGGLE_TRIGGER]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  payload: (ctx, e) => {
                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index).map((t) => ({ ...t, expanded: false })),
                        { ...ctx.payload.triggers[e.index], expanded: !ctx.payload.triggers[e.index].expanded },
                        ...ctx.payload.triggers.slice(e.index + 1).map((t) => ({ ...t, expanded: false })),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.ADD_TRIGGER]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index + 1).map((t) => ({ ...t, expanded: false })),
                        { ...emptyTrigger },
                        ...ctx.payload.triggers.slice(e.index + 1).map((t) => ({ ...t, expanded: false })),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.DELETE_TRIGGER]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    return {
                      ...ctx.payload,
                      triggers: [...ctx.payload.triggers.slice(0, e.index), ...ctx.payload.triggers.slice(e.index + 1)],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.SET_TRIGGER]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    const updatedTrigger = {
                      ...emptyTrigger, // always build up from the default values
                      ...ctx.payload.triggers[e.index],
                      [e.key]: e.value,
                    };

                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index),
                        { ...updatedTrigger, filter: serializeTrigger(updatedTrigger) },
                        ...ctx.payload.triggers.slice(e.index + 1),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.SET_SYNC_SELECTORS]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    const updatedTrigger = {
                      ...emptyTrigger, // always build up from the default values
                      ...ctx.payload.triggers[e.index],
                      syncSelectors: e.value,
                    };
                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index),
                        { ...updatedTrigger, filter: serializeTrigger(updatedTrigger) },
                        ...ctx.payload.triggers.slice(e.index + 1),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.SET_SELECTOR]: {
              target: STATES.MANAGE_TRIGGERS,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    const currentTrigger = ctx.payload.triggers[e.index];
                    const updatedTrigger = {
                      ...currentTrigger,
                    };
                    if (updatedTrigger.syncSelectors === true) {
                      updatedTrigger.phoneSelector = e.value;
                      updatedTrigger.tabletSelector = e.value;
                      updatedTrigger.desktopSelector = e.value;
                    } else if (e.device) {
                      updatedTrigger[`${e.device.toLowerCase()}Selector`] = e.value;
                    }

                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index),
                        { ...updatedTrigger, filter: serializeTrigger(updatedTrigger) },
                        ...ctx.payload.triggers.slice(e.index + 1),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.SET_INTERACTIVE]: {
              actions: [
                (ctx, e) => {
                  changeInteractiveMode(e.value, 'element-selector-frame');
                },
              ],
            },
            [GOAL_ACTIONS.CHANGE_POPUPS_STATE]: {
              actions: [
                assign({
                  hidePopups: (ctx) => !ctx.hidePopups,
                }),
                (ctx) => {
                  changePopupsState(ctx.hidePopups, 'element-selector-frame');
                },
              ],
            },
            [GOAL_ACTIONS.NEXT]: [
              {
                target: STATES.INITIALIZE_VALUE,
                cond: (ctx) => !ctx.dirty,
              },
              {
                target: STATES.SAVING_GOAL,
                cond: 'goalValid',
              },
            ],
          },
        },
        [STATES.MANAGE_VALUE]: {
          entry: assign({
            errors: (ctx) => getValidationErrors(valueSchema, ctx.payload),
          }),
          on: {
            [GOAL_ACTIONS.SET_SELECTOR]: {
              target: STATES.MANAGE_VALUE,
              actions: [
                assign({
                  dirty: true,
                  payload: (ctx, e) => {
                    const index = e.index || 0;
                    const currentTrigger = ctx.payload.triggers[index];
                    const updatedTrigger = {
                      ...currentTrigger,
                    };
                    if (updatedTrigger.elementValue.syncSelectors === true) {
                      updatedTrigger.elementValue.condition.selectorForPhone = e.value;
                      updatedTrigger.elementValue.condition.selectorForTablet = e.value;
                      updatedTrigger.elementValue.condition.selectorForDesktop = e.value;
                    } else if (e.device) {
                      updatedTrigger.elementValue.condition[`selectorFor${capitalize(e.device.toLowerCase())}`] =
                        e.value;
                    }

                    return {
                      ...ctx.payload,
                      triggers: [
                        ...ctx.payload.triggers.slice(0, e.index),
                        { ...updatedTrigger },
                        ...ctx.payload.triggers.slice(e.index + 1),
                      ],
                    };
                  },
                }),
              ],
            },
            [GOAL_ACTIONS.SET_SYNC_VALUES]: {
              target: STATES.MANAGE_VALUE,
              actions: assign({
                payload: (ctx, e) => ({
                  ...ctx.payload,
                  syncValues: e.value,
                }),
              }),
            },
            [GOAL_ACTIONS.SET_TRIGGER]: {
              target: STATES.MANAGE_VALUE,
              actions: assign({
                payload: (ctx, e) => {
                  return {
                    ...ctx.payload,
                    triggers: ctx.payload.triggers.map((trigger, index) => {
                      if (index === e.index || e.index === null) {
                        if (e.key === 'valueType' && !trigger?.[`${e.value.toLowerCase()}Value`]) {
                          const valueKey = `${e.value.toLowerCase()}Value`;
                          return {
                            ...trigger,
                            [e.key]: e.value,
                            [valueKey]: getDefaultGoalValue(e.value, ctx),
                          };
                        }
                        return {
                          ...trigger,
                          [e.key]: e.value,
                        };
                      }
                      return trigger;
                    }),
                  };
                },
              }),
            },
            [GOAL_ACTIONS.SET_TRIGGER_VALUE]: {
              target: STATES.MANAGE_VALUE,
              actions: assign({
                payload: (ctx, e) => {
                  return {
                    ...ctx.payload,
                    triggers: ctx.payload.triggers.map((trigger, index) => {
                      if (index === e.index || e.index === null) {
                        return {
                          ...trigger,
                          [`${e.trigger.valueType.toLowerCase()}Value`]: {
                            ...(trigger[`${e.trigger.valueType.toLowerCase()}Value`] || {}),
                            [e.key]: e.value,
                          },
                        };
                      }

                      return trigger;
                    }),
                  };
                },
              }),
            },
            [GOAL_ACTIONS.SET_INTERACTIVE]: {
              target: STATES.MANAGE_VALUE,
              actions: [
                (ctx, e) => {
                  changeInteractiveMode(e.value, 'element-selector-frame');
                },
              ],
            },
            [GOAL_ACTIONS.CHANGE_POPUPS_STATE]: {
              target: STATES.MANAGE_VALUE,
              actions: [
                assign({
                  hidePopups: (ctx) => !ctx.hidePopups,
                }),
                (ctx) => {
                  changePopupsState(ctx.hidePopups, 'element-selector-frame');
                },
              ],
            },
            [GOAL_ACTIONS.BACK]: [
              {
                target: STATES.REDIRECT_EDIT,
                cond: 'isNewGoalFlow',
              },
              {
                target: STATES.INITIALIZE_EDIT,
              },
            ],
            [GOAL_ACTIONS.NEXT]: [
              {
                target: STATES.COMPLETED,
                cond: 'onlyEcommerceOrApiTriggers',
              },
              {
                target: STATES.SAVING_VALUE,
                cond: 'valueValid',
              },
            ],
          },
        },
        [STATES.SAVING_VALUE]: {
          on: {
            [GOAL_ACTIONS.VALUE_SUCCESS]: [
              {
                target: STATES.COMPLETED,
              },
            ],
            [GOAL_ACTIONS.VALUE_ERROR]: [
              {
                target: STATES.MANAGE_VALUE,
              },
            ],
          },
        },
        [STATES.SAVING_GOAL]: {
          on: {
            [GOAL_ACTIONS.GOAL_SUCCESS]: [
              {
                target: STATES.INITIALIZE_VALUE,
                actions: [
                  assign({
                    nextText: (ctx) => ctx.originalNextText,
                    goalId: (ctx, e) => e.goalId,
                    dirty: false,
                  }),
                ],
              },
            ],
            [GOAL_ACTIONS.GOAL_ERROR]: {
              target: STATES.ERRORED,
              actions: assign({
                nextText: (ctx) => ctx.originalNextText,
              }),
            },
          },
          after: {
            5000: {
              target: STATES.TIMED_OUT,
              actions: assign({
                nextText: (ctx) => ctx.originalNextText,
              }),
            },
          },
        },
        [STATES.ERRORED]: {
          always: [
            {
              target: STATES.MANAGE_VALUE,
              cond: 'isValueFlow',
            },
            {
              target: STATES.MANAGE_TRIGGERS,
            },
          ],
        },
        [STATES.TIMED_OUT]: {
          always: [
            {
              target: STATES.MANAGE_VALUE,
              cond: 'isValueFlow',
            },
            {
              target: STATES.MANAGE_TRIGGERS,
            },
          ],
        },
        [STATES.COMPLETED]: {
          type: 'final',
          data: (ctx) => ({
            goalId: ctx.goalId,
            nextPage: ctx.nextPage,
          }),
        },
        [STATES.CANCELLED]: {
          type: 'final',
          data: (ctx) => {
            return {
              goal: contextToArgs(ctx),
              reason: ctx.cancelReason,
            };
          },
        },
      },
      on: {
        // global actions
        [GOAL_ACTIONS.BACK]: {
          target: STATES.CANCELLED,
          actions: [
            assign({
              cancelReason: 'BACK',
            }),
          ],
        },
        [GOAL_ACTIONS.CANCEL]: {
          target: STATES.CANCELLED,
          actions: [
            assign({
              cancelReason: 'CANCEL',
            }),
          ],
        },
        [GOAL_ACTIONS.SELECT_SITE]: {
          actions: [
            assign({
              payload: (ctx, e) => ({
                ...ctx.payload,
                siteId: e.site.id,
              }),
            }),
          ],
        },
        [GOAL_ACTIONS.CHANGE]: {
          actions: [
            assign({
              dirty: true,
              payload: (ctx, e) => ({
                ...ctx.payload,
                [e.key]: e.value,
              }),
            }),
          ],
        },
      },
    },
    {
      guards: {
        goalValid: (context) => {
          try {
            goalSchema.validateSync(context.payload);
            return true;
          } catch {
            return false;
          }
        },
        valueValid: (context) => {
          try {
            valueSchema.validateSync(context.payload);
            return true;
          } catch {
            return false;
          }
        },
        isValueFlow: () => {
          return window.location.pathname.endsWith('edit-value');
        },
        isNewGoalFlow: () => {
          return window.location.pathname.endsWith('goals/new');
        },
        onlyEcommerceOrApiTriggers: (context) => {
          const triggers = (context.payload?.triggers ?? []).filter((t) => !t.deletedAt);
          return triggers.length > 0 && triggers.every((t) => [ECOMMERCE_EVENT, API].includes(t.trigger));
        },
      },
    },
  );
};

export const newGoalMachine = createGoalMachine();
export const editGoalMachine = createGoalMachine(STATES.INITIALIZE_EDIT);
export const valueMachine = createGoalMachine(STATES.INITIALIZE_VALUE);
